import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Save } from '@mui/icons-material';
import apiService from '../../services/api';
import { CompleteUser } from '../../types';
import { toast } from 'react-toastify';

const schema = yup.object().shape({
  balance: yup.number().min(0, 'Balance must be at least 0').required('Balance is required'),
  reason: yup.string().optional(),
});

type FormData = {
  balance: number;
  reason: string;
};

interface SetWalletModalProps {
  user: CompleteUser | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const SetWalletModal: React.FC<SetWalletModalProps> = ({
  user,
  open,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = React.useState(false);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      balance: user?.wallet || 0,
      reason: '',
    },
  });

  const balanceValue = watch('balance');

  React.useEffect(() => {
    if (open && user) {
      reset({
        balance: user.wallet || 0,
        reason: '',
      });
    }
  }, [open, user, reset]);

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    setLoading(true);
    try {
      const result = await apiService.setUserWallet({
        userId: user.id,
        balance: data.balance,
        reason: data.reason || undefined,
      });
      
      if (result.success) {
        toast.success('Wallet balance set successfully');
        onSuccess();
        onClose();
      } else {
        toast.error(result.message || 'Failed to set wallet balance');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'An error occurred while setting wallet balance');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ET', {
      style: 'currency',
      currency: 'ETB',
    }).format(amount);
  };

  if (!user) return null;

  const balanceChange = user.wallet - (balanceValue || user.wallet);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Typography variant="h6">Set Wallet Balance</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ mt: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Setting balance for user: <strong>{user.username}</strong>
            <br />
            Current Balance: <strong>{formatCurrency(user.wallet)}</strong>
          </Alert>
          
          <Controller
            name="balance"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="New Balance"
                type="number"
                placeholder="0.00"
                error={!!errors.balance}
                helperText={errors.balance?.message}
                InputProps={{
                  startAdornment: 'ETB ',
                }}
                sx={{ mb: 2 }}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;
                  field.onChange(value);
                }}
              />
            )}
          />

          {balanceValue !== undefined && balanceValue !== user.wallet && (
            <Alert 
              severity={balanceChange > 0 ? 'warning' : balanceChange < 0 ? 'success' : 'info'} 
              sx={{ mb: 2 }}
            >
              {balanceChange > 0 ? (
                <>Balance will decrease by: <strong>{formatCurrency(balanceChange)}</strong></>
              ) : balanceChange < 0 ? (
                <>Balance will increase by: <strong>{formatCurrency(Math.abs(balanceChange))}</strong></>
              ) : (
                <>Balance will remain unchanged</>
              )}
            </Alert>
          )}

          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Reason (Optional)"
                placeholder="Enter reason for this adjustment"
                error={!!errors.reason}
                helperText={errors.reason?.message || 'Optional reason for the wallet balance adjustment'}
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <Save />}
        >
          {loading ? 'Setting...' : 'Set Balance'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SetWalletModal;
